import React, { ReactNode } from 'react'
import Paper from '@mui/material/Paper'
import {
  Pagination as MuiPagination,
  Table as MuiTable,
  TableBody,
  Typography,
} from '@mui/material'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import theme from 'lib/theme'
import { TooltipText } from 'components/TooltipText/TooltipText'
import { useTranslation } from 'react-i18next'
import { Pagination } from 'types/model/common'
import { HeaderTableCell } from './Table.styled'

export interface Column<T> {
  id: T
  label: string
  minWidth: number
  align?: 'right'
  cellWidth?: string
}

interface TableProps<T> {
  pagination?: Pagination
  columns: Column<T>[]
  children: ReactNode
  page?: number
  handleChangePage?: (newPage: number) => void
  ariaLabel: string
  tableHeight: string
}

export const Table = <T extends string>({
  pagination,
  columns,
  children,
  page,
  handleChangePage,
  ariaLabel,
  tableHeight,
}: TableProps<T>) => {
  const { t } = useTranslation()
  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        [theme.breakpoints.between('xs', 'sm')]: {
          marginTop: theme.spacing(1),
        },
        border: 'none',
        boxShadow: 'none',
      }}>
      <TableContainer
        sx={{
          maxHeight: tableHeight,
        }}>
        <MuiTable stickyHeader aria-label={ariaLabel}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <HeaderTableCell
                  index={index}
                  minWidth={column.minWidth}
                  key={column.id}
                  align={column.align}
                  style={{
                    minWidth: column.minWidth,
                  }}>
                  {column.label === t('orderProduct.rowPrice') ||
                  column.label === t('orderProduct.charged') ? (
                    <TooltipText
                      text={
                        column.label === t('orderProduct.rowPrice')
                          ? t('orderProduct.rowPriceTooltip')
                          : t('orderProduct.chargedTooltip')
                      }>
                      <Typography
                        sx={{
                          '&:hover': { cursor: 'pointer' },
                          textTransform: 'uppercase',
                          transition: '0.2s ease-in-out margin-left',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: theme.palette.text.secondary,
                        }}>
                        {column.label.toUpperCase()}
                      </Typography>
                    </TooltipText>
                  ) : (
                    column.label
                  )}
                </HeaderTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{children}</TableBody>
        </MuiTable>
      </TableContainer>
      {pagination && page && handleChangePage && (
        <MuiPagination
          page={page}
          count={pagination?.totalPages ?? 0}
          onChange={(_e, page) => handleChangePage(page)}
          color='secondary'
          sx={{
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(3),
          }}
        />
      )}
    </Paper>
  )
}
